import React, { ReactElement, FC } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';
import { ScheduledEventForm } from '../models/ScheduledEventDto';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { LocalizedDatePicker } from '@thingslog/ui-components';
import { EventConfig } from './EventConfig';
import { ChronoUnit } from '@thingslog/repositories';
import { useTranslation } from 'react-i18next';

const ScheduledEventFormGeneralFields: FC<ScheduledEventFormGeneralFieldsProps> = ({
  form,
}: ScheduledEventFormGeneralFieldsProps) => {
  const {
    register,
    control,
    formState: { errors },
    watch,
  } = form;

  const { t } = useTranslation();

  const repeat = watch('repeat');
  const eventType = watch('event.@type');

  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <>
      {/* From Date */}
      <div className="col-span-4 md:col-span-1">
        <Controller
          name="fromDate"
          control={control}
          rules={{ required: true }}
          render={(props: {
            field: ControllerRenderProps<ScheduledEventForm, 'fromDate'>;
            fieldState: ControllerFieldState;
            formState: UseFormStateReturn<ScheduledEventForm>;
          }): ReactElement => {
            const toDate = watch('toDate');

            return (
              <>
                <LocalizedDatePicker
                  label={t('task_scheduler_form_from_date')}
                  {...props.field}
                  maxDate={toDate}
                  inputFormat="dd/MM/yyyy"
                  renderInputTextField={{
                    size: 'small',
                    style: { backgroundColor: 'white', width: '100%' },
                  }}
                />
                {errors.fromDate && (
                  <div className="pl-4 text-sm font-normal text-red-600">
                    {errors.fromDate.message}
                  </div>
                )}
              </>
            );
          }}
        ></Controller>
      </div>
      {/* To Date */}
      <div className="col-span-4 md:col-span-1">
        <Controller
          name="toDate"
          control={control}
          rules={{ required: true }}
          render={(props: {
            field: ControllerRenderProps<ScheduledEventForm, 'toDate'>;
            fieldState: ControllerFieldState;
            formState: UseFormStateReturn<ScheduledEventForm>;
          }): ReactElement => (
            <>
              <LocalizedDatePicker
                label={t('task_scheduler_form_to_date')}
                {...props.field}
                inputFormat="dd/MM/yyyy"
                renderInputTextField={{
                  size: 'small',
                  style: { backgroundColor: 'white', width: '100%' },
                }}
              />
              {errors.toDate && (
                <div className="pl-4 text-sm font-normal text-red-600">{errors.toDate.message}</div>
              )}
            </>
          )}
        ></Controller>
      </div>
      {/* Execution Hour */}
      <div className="col-span-4 md:col-span-1">
        <FormControl fullWidth size="small" error={!!errors.executionHour}>
          <InputLabel id="execution-hour-label">
            {t('task_scheduler_form_execution_hour')}
          </InputLabel>
          <Controller
            name="executionHour"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'executionHour'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId="execution-hour-label"
                label={t('task_scheduler_form_execution_hour')}
              >
                {Array.from({ length: 24 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.executionHour && <FormHelperText>{errors.executionHour.message}</FormHelperText>}
        </FormControl>
      </div>
      {/* Execution Minute */}
      <div className="col-span-4 md:col-span-1">
        <FormControl fullWidth size="small" error={!!errors.executionMinute}>
          <InputLabel id="execution-minute-label">
            {t('task_scheduler_form_execution_minute')}
          </InputLabel>
          <Controller
            name="executionMinute"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'executionMinute'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId="execution-minute-label"
                label={t('task_scheduler_form_execution_minute')}
              >
                {Array.from({ length: 60 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.executionMinute && (
            <FormHelperText>{errors.executionMinute.message}</FormHelperText>
          )}
        </FormControl>
      </div>
      <div className="col-span-4">
        <EventConfig selectedEventType={eventType} form={form} />
      </div>

      <div className="col-span-4 grid grid-cols-1 md:grid-cols-3 gap-x-2 gap-y-4">
        {/* Repeat */}
        <div className="col-span-1">
          <FormGroup>
            <FormControlLabel
              {...register('repeat')}
              control={<Checkbox />}
              label={t('task_scheduler_form_repeat')}
            />
          </FormGroup>
        </div>
        {/* Period */}
        <div>
          <TextField
            label={t('task_scheduler_form_period')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            variant="outlined"
            {...register('period', { valueAsNumber: true })}
            disabled={!repeat}
            type="number"
            error={!!errors.period}
            size="small"
            helperText={errors.period?.message ?? ''}
            inputProps={{ min: 1 }}
          />
        </div>
        {/* Temporal */}
        <div className="col-span-1">
          <FormControl fullWidth size="small" disabled={!repeat}>
            <InputLabel id="temporal-label">{t('task_scheduler_form_temporal')}</InputLabel>
            <Controller
              name="temporal"
              control={control}
              render={({
                field,
              }: {
                field: ControllerRenderProps<ScheduledEventForm, 'temporal'>;
                fieldState: ControllerFieldState;
                formState: UseFormStateReturn<ScheduledEventForm>;
              }): ReactElement => (
                <Select
                  {...field}
                  labelId="temporal-label"
                  label={t('task_scheduler_form_temporal')}
                >
                  <MenuItem value="">
                    <em>{t('scheduled_task_event_form_select')}</em>
                  </MenuItem>
                  {Object.values(ChronoUnit).map((unit: ChronoUnit) => (
                    <MenuItem key={unit} value={unit}>
                      {capitalizeFirstLetter(t(`${unit.toLowerCase()}`))}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.temporal && <FormHelperText>{errors.temporal.message}</FormHelperText>}
          </FormControl>
        </div>
      </div>
    </>
  );
};

interface ScheduledEventFormGeneralFieldsProps {
  form: UseFormReturn<ScheduledEventForm, unknown, undefined>;
}

export default ScheduledEventFormGeneralFields;
